import { useTranslation } from 'react-i18next';
import type { PatientResponse, StaffResponse } from '../../api/generated';
import type { RoomProperty } from '../../store/roomProperties/roomPropertiesSlice';
import { useHasPermission } from '../../hooks/useHasPermission';
import { PERMISSIONS } from '../../constants/permissions';
import EditIcon from '../../assets/icons/edit.svg?react';
import { EMPTY_VALUE } from '../../constants/displayConstants';
import type { CompetenceNameById } from './preferredStaffHelpers';
import { PreferredStaffList } from './PreferredStaffList';
import styles from './PatientDetailsCard.module.css';

interface PatientDetailsViewProps {
  patient: PatientResponse;
  patientRoomNeeds: RoomProperty[];
  patientPreferredStaff: StaffResponse[];
  hasStoredPreferredStaff: boolean;
  competenceNameById: CompetenceNameById;
  isStaffLoaded: boolean;
  hasStaffError: boolean;
  onRetryStaff: () => void;
  onEditClick: () => void;
}

export const PatientDetailsView = ({
  patient,
  patientRoomNeeds,
  patientPreferredStaff,
  hasStoredPreferredStaff,
  competenceNameById,
  isStaffLoaded,
  hasStaffError,
  onRetryStaff,
  onEditClick
}: PatientDetailsViewProps) => {
  const { t } = useTranslation();
  const canEditPatient = useHasPermission(PERMISSIONS.PatientWrite);
  const hasNote = Boolean(patient.note);
  const hasRoomNeeds = patientRoomNeeds.length > 0;

  const fields: { label: string; value: string }[] = [
    { label: t('patient-detail-email'), value: patient.email ?? EMPTY_VALUE },
    { label: t('patient-detail-phone'), value: patient.phoneNumber ?? EMPTY_VALUE },
    { label: t('patient-detail-cpr'), value: patient.cprNumber },
    { label: t('patient-detail-status'), value: EMPTY_VALUE }
  ];

  return (
    <>
      <div className={styles.cardHeader}>
        <span className={styles.cardHeaderLabel}>{t('details-card-title')}</span>
        {canEditPatient && (
          <button className={styles.editButton} onClick={onEditClick}>
            <EditIcon />
          </button>
        )}
      </div>

      <h2 className={styles.patientName}>{patient.name}</h2>

      {fields.map((field) => (
        <div key={field.label} className={styles.field}>
          <span className={styles.fieldLabel}>{field.label}</span>
          <span className={styles.fieldValue}>{field.value}</span>
        </div>
      ))}

      <div className={hasRoomNeeds ? styles.fieldBlock : styles.field}>
        <span className={hasRoomNeeds ? styles.fieldLabelSmall : styles.fieldLabel}>
          {t('patient-detail-room-need')}
        </span>
        {hasRoomNeeds ? (
          <div className={styles.pills}>
            {patientRoomNeeds.map((rp) => (
              <span key={rp.id} className={styles.pill}>
                {rp.name}
              </span>
            ))}
          </div>
        ) : (
          <span className={styles.fieldValue}>{EMPTY_VALUE}</span>
        )}
      </div>

      <PreferredStaffList
        preferredStaff={patientPreferredStaff}
        hasStoredPreferredStaff={hasStoredPreferredStaff}
        competenceNameById={competenceNameById}
        isStaffLoaded={isStaffLoaded}
        hasStaffError={hasStaffError}
        onRetryStaff={onRetryStaff}
      />

      <div className={hasNote ? styles.fieldBlock : styles.field}>
        <span className={hasNote ? styles.fieldLabelSmall : styles.fieldLabel}>
          {t('patient-detail-comment')}
        </span>
        {hasNote ? (
          <span className={styles.commentText}>{patient.note}</span>
        ) : (
          <span className={styles.fieldValue}>{EMPTY_VALUE}</span>
        )}
      </div>
    </>
  );
};
